/**
 * Several options on one bid, in the office — "like separate bids in one" (owner, Nov 3 2026).
 *
 * One card per option: a title, a line of copy, its photos or mockups, and its own priced lines. A line can carry a
 * heading inside the option ("Demo", "Planting") and each heading shows its own subtotal, so a long option reads as
 * parts rather than a wall. Add, reorder, remove. "Starts ticked" is what their page opens on; they can change it.
 *
 * The bid's own lines stay where they always were, in the list below this — they are what the customer gets
 * whichever option they pick (the travel line, a lawn plan, the mow). A bid with no options never draws this at all
 * beyond the one button that makes the first one.
 *
 * Every write goes through the ordinary bid routes with a `bid_option_id` on it, so an option is a box to put lines
 * and photos in, not a second kind of bid. `lineExtra` draws something under a line — the per-line size picker (a
 * bush at 1, 3 or 15 gallon) hangs there, which is why a size choice lives on the line and not on the option.
 *
 * Server: services/bidOptions.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Plus, Trash2, Edit2, ArrowUp, ArrowDown, Image as ImageIcon, X } from 'lucide-react';
import api from '../services/api';

export type BuilderLine = {
  id: number; description: string; quantity: number; unit_price: number; total: number;
  unit?: string | null; is_optional?: number; is_selected?: number; quote_on_request?: number; section?: string | null;
};
export type BuilderOption = {
  id: number; title: string; blurb: string | null; is_selected: boolean; subtotal: number; optional_room: number;
  lines: BuilderLine[];
  sections: { name: string | null; subtotal: number; lines: BuilderLine[] }[];
  media: { id: number; file_name: string | null; mime_type: string; data: string }[];
};

const fmtMoney = (n: number | string | null | undefined) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
};
const emptyLine = { description: '', quantity: '1', unit_price: '', section: '', is_optional: false };

export default function BidOptionBuilder({ quoteId, options, canEdit, onRefresh, lineExtra }: {
  quoteId: number;
  options: BuilderOption[];
  canEdit: boolean;
  onRefresh: () => void;
  lineExtra?: (li: BuilderLine) => React.ReactNode;
}) {
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<{ id: number; title: string; blurb: string } | null>(null);
  const [lineFor, setLineFor] = useState<number | null>(null);
  const [lineForm, setLineForm] = useState({ ...emptyLine });
  const [editLine, setEditLine] = useState<any | null>(null);

  const qc = useQueryClient();
  const fail = (what: string) => (e: any) => { toast.error(e?.response?.data?.message || `Could not ${what}`); };
  // Every write here changes what the bid comes to, so the list behind the sheet is refreshed with it.
  const done = (msg?: string) => (r: any) => { if (r?.data?.message || msg) toast.success(r?.data?.message || msg); onRefresh(); qc.invalidateQueries('quotes'); };

  const addOption = useMutation(() => api.post(`/quotes/${quoteId}/bid-options`, { title: newTitle.trim() }), {
    onSuccess: (r) => { setAdding(false); setNewTitle(''); done('Option added')(r); }, onError: fail('add that option'),
  });
  const saveOption = useMutation((body: any) => api.put(`/quotes/bid-options/${body.id}`, body), {
    onSuccess: (r) => { setEditing(null); done()(r); }, onError: fail('save that option'),
  });
  const removeOption = useMutation(({ id, keep }: { id: number; keep: boolean }) =>
    api.delete(`/quotes/bid-options/${id}${keep ? '?keep_lines=1' : ''}`), { onSuccess: done(), onError: fail('remove that option') });
  const reorder = useMutation((ids: number[]) => api.put(`/quotes/${quoteId}/bid-options/order`, { ids }), {
    onSuccess: done(), onError: fail('reorder the options'),
  });
  const addLine = useMutation(({ optionId }: { optionId: number }) => api.post(`/quotes/${quoteId}/line-items`, {
    description: lineForm.description.trim(),
    quantity: Number(lineForm.quantity || 1),
    unit_price: Number(lineForm.unit_price || 0),
    is_optional: lineForm.is_optional ? 1 : 0,
    bid_option_id: optionId,
    section: lineForm.section.trim() || null,
  }), { onSuccess: (r) => { setLineFor(null); setLineForm({ ...emptyLine }); done()(r); }, onError: fail('add that line') });
  const saveLine = useMutation((li: any) => api.put(`/quotes/line-items/${li.id}`, {
    description: li.description, quantity: Number(li.quantity || 1), unit_price: Number(li.unit_price || 0),
    is_optional: li.is_optional ? 1 : 0, section: String(li.section || '').trim() || null,
  }), { onSuccess: (r) => { setEditLine(null); done()(r); }, onError: fail('save that line') });
  const dropLine = useMutation((id: number) => api.delete(`/quotes/line-items/${id}`), { onSuccess: done(), onError: fail('remove that line') });
  const upload = useMutation(({ optionId, data, file_name, mime_type }: any) =>
    api.post(`/quotes/${quoteId}/media`, { data, file_name, mime_type, bid_option_id: optionId }),
    { onSuccess: done(), onError: fail('upload that photo') });
  const dropMedia = useMutation((id: number) => api.delete(`/quotes/media/${id}`), { onSuccess: done(), onError: fail('remove that photo') });

  const pickPhoto = (optionId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      const reader = new FileReader();
      reader.onload = () => upload.mutate({ optionId, data: String(reader.result), file_name: f.name, mime_type: f.type || 'image/jpeg' });
      reader.readAsDataURL(f);
    }
  };

  const move = (i: number, by: number) => {
    const ids = options.map(o => o.id);
    const j = i + by;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder.mutate(ids);
  };

  if (!options.length) {
    return canEdit ? (
      <div className="mt-3">
        {adding ? (
          <div className="bg-brand-50 rounded-lg p-3 space-y-2">
            <p className="text-[11px] text-gray-600">
              Several ways to do the job on one bid — each with its own photos and prices. They read them all and tick
              the one they want. The lines below stay on the bid itself, whichever they pick.
            </p>
            <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)}
              className="input text-sm w-full" placeholder='Name the first option — "Full rebuild", "Patch and seed"' />
            <div className="flex gap-2">
              <button onClick={() => addOption.mutate()} disabled={!newTitle.trim() || addOption.isLoading} className="btn-primary text-xs py-1 px-3">Add option</button>
              <button onClick={() => { setAdding(false); setNewTitle(''); }} className="btn-secondary text-xs py-1 px-3">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)}
            className="text-xs px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:border-brand-400 hover:text-brand-700 flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Offer more than one option on this bid
          </button>
        )}
      </div>
    ) : null;
  }

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {options.length} option{options.length === 1 ? '' : 's'} on this bid
        </p>
        <p className="text-[11px] text-gray-500">They tick the one they want — one or more.</p>
      </div>

      {options.map((o, i) => (
        <div key={o.id} className={`rounded-xl border p-3 ${o.is_selected ? 'border-brand-300 bg-brand-50/60' : 'border-gray-200 bg-white'}`}>
          {editing?.id === o.id ? (
            <div className="space-y-2">
              <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="input text-sm w-full" placeholder="Title" />
              <textarea value={editing.blurb} onChange={e => setEditing({ ...editing, blurb: e.target.value })} rows={3}
                className="input text-sm w-full resize-none" placeholder="A line or two for the customer — what this way of doing it gets them" />
              <div className="flex gap-2">
                <button onClick={() => saveOption.mutate({ id: o.id, title: editing.title, blurb: editing.blurb })}
                  disabled={!editing.title.trim() || saveOption.isLoading} className="btn-primary text-xs py-1 px-3">Save</button>
                <button onClick={() => setEditing(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Option {i + 1}</p>
                <p className="text-sm font-semibold text-gray-900">{o.title}</p>
                {o.blurb && <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-line">{o.blurb}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-sm font-bold text-gray-900 tabular-nums">{fmtMoney(o.subtotal)}</span>
                {canEdit && (
                  <>
                    <button title="Move up" onClick={() => move(i, -1)} disabled={i === 0 || reorder.isLoading}
                      className="p-1 text-gray-400 hover:text-brand-600 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button title="Move down" onClick={() => move(i, 1)} disabled={i === options.length - 1 || reorder.isLoading}
                      className="p-1 text-gray-400 hover:text-brand-600 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button title="Edit" onClick={() => setEditing({ id: o.id, title: o.title, blurb: o.blurb || '' })}
                      className="p-1 text-gray-500 hover:text-brand-600"><Edit2 className="h-3.5 w-3.5" /></button>
                    <button title="Remove this option"
                      onClick={() => {
                        if (!window.confirm(`Remove "${o.title}"${o.lines.length ? ` and its ${o.lines.length} line${o.lines.length === 1 ? '' : 's'}` : ''}?`)) return;
                        const keep = o.lines.length > 0 && window.confirm('Keep its lines on the bid itself? OK keeps them, Cancel removes them with the option.');
                        removeOption.mutate({ id: o.id, keep });
                      }}
                      className="p-1 text-gray-500 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          )}

          {canEdit && (
            <label className="flex items-center gap-2 text-[11px] text-gray-600 mt-2">
              <input type="checkbox" checked={o.is_selected} className="rounded text-brand-600"
                onChange={e => saveOption.mutate({ id: o.id, is_selected: e.target.checked })} />
              Starts ticked on their page
            </label>
          )}

          {(o.media.length > 0 || canEdit) && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-2">
                {o.media.map(m => (
                  <div key={m.id} className="relative group">
                    {String(m.mime_type || '').startsWith('image/')
                      ? <img src={m.data} alt={m.file_name || o.title} className="h-16 w-20 object-cover rounded-md border border-gray-200" />
                      : <span className="h-16 w-20 flex items-center justify-center rounded-md border border-gray-200 text-[10px] text-gray-500 px-1 text-center">{m.file_name || 'File'}</span>}
                    {canEdit && (
                      <button onClick={() => { if (window.confirm('Remove this photo?')) dropMedia.mutate(m.id); }}
                        className="absolute -top-1.5 -right-1.5 bg-white border border-gray-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                        <X className="h-3 w-3 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <label className="h-16 w-20 flex flex-col items-center justify-center rounded-md border border-dashed border-gray-300 text-[10px] text-gray-500 cursor-pointer hover:border-brand-400 hover:text-brand-700">
                    <ImageIcon className="h-4 w-4 mb-0.5" />
                    {upload.isLoading ? 'Uploading' : 'Photo'}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={pickPhoto(o.id)} />
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="mt-2 space-y-2">
            {o.sections.map((s, si) => (
              <div key={s.name || `s${si}`}>
                {s.name && (
                  <div className="flex items-baseline justify-between">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{s.name}</p>
                    <p className="text-[11px] font-semibold text-gray-600 tabular-nums">{fmtMoney(s.subtotal)}</p>
                  </div>
                )}
                <div className="space-y-1">
                  {s.lines.map(li => (
                    editLine?.id === li.id ? (
                      <div key={li.id} className="bg-brand-50 rounded-lg p-2 space-y-2">
                        <input value={editLine.description} onChange={e => setEditLine({ ...editLine, description: e.target.value })} className="input text-sm w-full" />
                        <div className="flex gap-2">
                          <input type="number" value={editLine.quantity} onChange={e => setEditLine({ ...editLine, quantity: e.target.value })} className="input text-sm w-20" placeholder="Qty" />
                          <input type="number" step="0.01" value={editLine.unit_price} onChange={e => setEditLine({ ...editLine, unit_price: e.target.value })} className="input text-sm flex-1" placeholder="Unit price" />
                          <input value={editLine.section || ''} onChange={e => setEditLine({ ...editLine, section: e.target.value })} className="input text-sm w-32" placeholder="Heading" />
                        </div>
                        <label className="flex items-center gap-2 text-[11px] text-gray-600">
                          <input type="checkbox" checked={!!editLine.is_optional} onChange={e => setEditLine({ ...editLine, is_optional: e.target.checked })} className="rounded text-brand-600" />
                          An extra they tick inside this option
                        </label>
                        <div className="flex gap-2">
                          <button onClick={() => saveLine.mutate(editLine)} disabled={saveLine.isLoading || !editLine.description.trim()} className="btn-primary text-xs py-1 px-3">Save</button>
                          <button onClick={() => setEditLine(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div key={li.id} className={`flex items-center justify-between rounded-lg px-2 py-1.5 group ${li.is_optional ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-800 truncate">{li.description}</p>
                          {Number(li.quantity) !== 1 && <p className="text-[11px] text-gray-500">{li.quantity} × {fmtMoney(li.unit_price)}</p>}
                          {lineExtra && lineExtra(li)}
                        </div>
                        {!!li.is_optional && <span className="text-[10px] text-blue-500 font-medium mr-2">extra</span>}
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">{fmtMoney(li.total)}</span>
                        {canEdit && (
                          <div className="flex ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditLine({ ...li })} className="p-1 text-gray-500 hover:text-brand-600"><Edit2 className="h-3.5 w-3.5" /></button>
                            <button onClick={() => { if (window.confirm('Remove this line?')) dropLine.mutate(li.id); }} className="p-1 text-gray-500 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        )}
                      </div>
                    )
                  ))}
                </div>
              </div>
            ))}
            {!o.lines.length && <p className="text-[11px] text-gray-500">No lines yet — add what this option costs.</p>}
          </div>

          {canEdit && (lineFor === o.id ? (
            <div className="bg-brand-50 rounded-lg p-2 mt-2 space-y-2">
              <input autoFocus value={lineForm.description} onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))}
                className="input text-sm w-full" placeholder="Description *" />
              <div className="flex gap-2">
                <input type="number" value={lineForm.quantity} onChange={e => setLineForm(f => ({ ...f, quantity: e.target.value }))} className="input text-sm w-20" placeholder="Qty" />
                <input type="number" step="0.01" value={lineForm.unit_price} onChange={e => setLineForm(f => ({ ...f, unit_price: e.target.value }))} className="input text-sm flex-1" placeholder="Unit price *" />
                <input value={lineForm.section} onChange={e => setLineForm(f => ({ ...f, section: e.target.value }))} className="input text-sm w-32" placeholder="Heading (optional)" />
              </div>
              <label className="flex items-center gap-2 text-[11px] text-gray-600">
                <input type="checkbox" checked={lineForm.is_optional} onChange={e => setLineForm(f => ({ ...f, is_optional: e.target.checked }))} className="rounded text-brand-600" />
                An extra they tick inside this option
              </label>
              <div className="flex gap-2">
                <button onClick={() => addLine.mutate({ optionId: o.id })}
                  disabled={addLine.isLoading || !lineForm.description.trim() || lineForm.unit_price === ''} className="btn-primary text-xs py-1 px-3">Add</button>
                <button onClick={() => { setLineFor(null); setLineForm({ ...emptyLine }); }} className="btn-secondary text-xs py-1 px-3">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => { setLineFor(o.id); setLineForm({ ...emptyLine }); }}
              className="mt-2 text-xs text-brand-700 font-medium hover:underline flex items-center gap-1">
              <Plus className="h-3 w-3" /> Add a line to this option
            </button>
          ))}
        </div>
      ))}

      {canEdit && (adding ? (
        <div className="bg-brand-50 rounded-lg p-3 space-y-2">
          <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)} className="input text-sm w-full" placeholder="Name the next option" />
          <div className="flex gap-2">
            <button onClick={() => addOption.mutate()} disabled={!newTitle.trim() || addOption.isLoading} className="btn-primary text-xs py-1 px-3">Add option</button>
            <button onClick={() => { setAdding(false); setNewTitle(''); }} className="btn-secondary text-xs py-1 px-3">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)}
          className="text-xs px-3 py-1.5 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:border-brand-400 hover:text-brand-700 flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Another option
        </button>
      ))}
    </div>
  );
}
